/**
 * Error Handling Utilities for Import/Export
 * 
 * This module provides standardized error handling and user-friendly
 * error messages for file size limits and memory management issues.
 */

export interface FileSizeError {
  type: 'file_too_large' | 'memory_limit_exceeded' | 'compressed_too_large' | 'uncompressed_estimate_too_large';
  fileSize: number;
  maxSize: number;
  fileType: 'compressed' | 'uncompressed';
  fileName: string;
  suggestion?: string;
}

export interface ErrorMessage {
  title: string;
  message: string;
  suggestion: string;
  severity: 'error' | 'warning' | 'info';
}

/**
 * Create user-friendly error messages for file size issues
 */
export const createFileSizeErrorMessage = (error: FileSizeError): ErrorMessage => {
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const fileSizeFormatted = formatFileSize(error.fileSize);
  const maxSizeFormatted = formatFileSize(error.maxSize);

  switch (error.type) {
    case 'file_too_large':
      return {
        title: 'File Too Large',
        message: `The file "${error.fileName}" (${fileSizeFormatted}) exceeds the maximum allowed size of ${maxSizeFormatted} for ${error.fileType} files.`,
        suggestion: error.suggestion || 'Please use a smaller file or compress it further to reduce the file size.',
        severity: 'error'
      };

    case 'memory_limit_exceeded':
      return {
        title: 'Memory Limit Exceeded',
        message: `Processing "${error.fileName}" would exceed browser memory limits. The file size (${fileSizeFormatted}) is too large for safe processing.`,
        suggestion: error.suggestion || 'Please use a smaller file or split the data into multiple smaller files.',
        severity: 'error'
      };

    case 'compressed_too_large':
      return {
        title: 'ZIP File Too Large',
        message: `The ZIP file "${error.fileName}" (${fileSizeFormatted}) exceeds the 512KB limit for compressed files. This limit ensures optimal browser performance.`,
        suggestion: error.suggestion || 'Please compress the ZIP file with maximum compression settings or split the data into multiple smaller ZIP files.',
        severity: 'error'
      };

    case 'uncompressed_estimate_too_large':
      return {
        title: 'Estimated Uncompressed Size Too Large',
        message: `The ZIP file "${error.fileName}" may contain uncompressed data larger than 1MB (estimated ${fileSizeFormatted}). This could cause browser memory issues.`,
        suggestion: error.suggestion || 'Please extract and re-compress the file with higher compression, or use a smaller dataset.',
        severity: 'error'
      };

    default:
      return {
        title: 'File Size Error',
        message: `There was an issue with the file "${error.fileName}".`,
        suggestion: 'Please check the file size and try again.',
        severity: 'error'
      };
  }
};

/**
 * Generate specific suggestions based on file type and size
 */
export const generateFileSizeSuggestions = (file: File, errorType: FileSizeError['type']): string => {
  const fileName = file.name.toLowerCase();
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);

  if (fileName.endsWith('.zip')) {
    switch (errorType) {
      case 'compressed_too_large':
        return `ZIP file is too large (${fileSizeMB}MB). Solutions:\n• Use maximum compression settings (9/Ultra compression)\n• Remove unnecessary files from the ZIP\n• Split data into multiple smaller ZIP files (under 512KB each)\n• Use 7-Zip or WinRAR for better compression`;
      case 'uncompressed_estimate_too_large':
        return `ZIP file may contain too much data when uncompressed. Solutions:\n• Extract and re-compress with maximum compression\n• Split into multiple smaller ZIP files\n• Remove large files that aren't needed\n• Use a different compression format (7z, RAR)`;
      default:
        return `For ZIP files, try:\n• Maximum compression settings\n• Splitting data into multiple smaller files\n• Removing unnecessary files\n• Using better compression tools`;
    }
  }

  if (fileName.endsWith('.csv')) {
    return `For CSV files, try: 1) Removing unnecessary columns, 2) Filtering the data to include only required rows, 3) Converting to a more efficient format like JSON, or 4) Splitting into multiple smaller CSV files.`;
  }

  if (fileName.endsWith('.json')) {
    return `For JSON files, try: 1) Removing unnecessary fields, 2) Using more compact JSON formatting, 3) Splitting into multiple smaller JSON files, or 4) Converting to a more efficient format.`;
  }

  return `Try reducing the file size by: 1) Removing unnecessary data, 2) Using compression, 3) Splitting into multiple smaller files, or 4) Using a more efficient file format.`;
};

/**
 * Create a comprehensive error object for file size validation
 */
export const createFileSizeError = (file: File, maxSize: number, errorType: FileSizeError['type']): FileSizeError => {
  const isCompressed = file.name.toLowerCase().match(/\.(zip|rar|7z|gz|bz2)$/);
  
  return {
    type: errorType,
    fileSize: file.size,
    maxSize: maxSize,
    fileType: isCompressed ? 'compressed' : 'uncompressed',
    fileName: file.name,
    suggestion: generateFileSizeSuggestions(file, errorType)
  };
};

/**
 * Format error message for display in UI components
 */
export const formatErrorForUI = (error: Error | FileSizeError): ErrorMessage => {
  // Check if it's already a FileSizeError
  if ('type' in error && 'fileSize' in error) {
    return createFileSizeErrorMessage(error as FileSizeError);
  }

  // Handle regular Error objects
  const errorMessage = error.message || 'An unknown error occurred';
  
  // Check for specific error patterns
  if (errorMessage.includes('File size') || errorMessage.includes('exceeds')) {
    return {
      title: 'File Size Error',
      message: errorMessage,
      suggestion: 'Please use a smaller file or check the file size limits.',
      severity: 'error'
    };
  }

  if (errorMessage.includes('memory') || errorMessage.includes('Memory')) {
    return {
      title: 'Memory Error',
      message: errorMessage,
      suggestion: 'Please use a smaller file to prevent browser memory issues.',
      severity: 'error'
    };
  }

  // Default error handling
  return {
    title: 'Import Error',
    message: errorMessage,
    suggestion: 'Please check the file and try again. If the problem persists, contact support.',
    severity: 'error'
  };
};

/**
 * Create a standardized error alert component props
 */
export const createErrorAlertProps = (error: Error | FileSizeError) => {
  const errorInfo = formatErrorForUI(error);
  
  return {
    severity: errorInfo.severity,
    title: errorInfo.title,
    message: errorInfo.message,
    suggestion: errorInfo.suggestion,
    action: errorInfo.severity === 'error' ? 'Please try again' : 'Continue'
  };
};

/**
 * Log error with context for debugging
 */
export const logFileSizeError = (error: FileSizeError, context?: string) => {
  console.group(`🚫 File Size Error${context ? ` - ${context}` : ''}`);
  console.error('File:', error.fileName);
  console.error('File Size:', `${(error.fileSize / (1024 * 1024)).toFixed(2)}MB`);
  console.error('Max Size:', `${(error.maxSize / (1024 * 1024)).toFixed(2)}MB`);
  console.error('Type:', error.type);
  console.error('File Type:', error.fileType);
  console.error('Suggestion:', error.suggestion);
  console.groupEnd();
};

export default {
  createFileSizeErrorMessage,
  generateFileSizeSuggestions,
  createFileSizeError,
  formatErrorForUI,
  createErrorAlertProps,
  logFileSizeError
};
